import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import { getHerdQueryOptions } from "@packages/api-client/hooks/livestockController";
import type { UpdateHerdMutationRequest } from "@packages/api-client/types/livestockController";
import { Action, type UserAccessProfile } from "@packages/shared-types";
import type { LivestockHerd } from "@packages/shared-types/livestock";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUpdateHerd } from "@/api/livestock";
import { PageHeader } from "@/components/PageHeader";
import { FormSkeleton } from "@/components/ui/skeleton";
import { hasPermission } from "@/hooks/useAccessControl";
import { ensureQueryDataSurvivingCancel } from "@/lib/ensureQueryDataSurvivingCancel";
import { ForbiddenError } from "@/lib/forbiddenError";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { HerdAnimalsSection } from "../-components/HerdAnimalsSection";
import { HerdForm } from "../-components/HerdForm";
import {
	HERD_DETAIL_KEYS,
	type HerdDetailKey,
} from "../-components/herdSummary";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/herds/$herdId/edit",
)({
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	// `?focus=` opens and focuses one planning detail — the saved-state "fill in
	// when you know" card links here so a skipped field lands ready to edit.
	validateSearch: (
		search: Record<string, unknown>,
	): { focus?: HerdDetailKey } => {
		const focus = search.focus;
		return typeof focus === "string" &&
			(HERD_DETAIL_KEYS as readonly string[]).includes(focus)
			? { focus: focus as HerdDetailKey }
			: {};
	},
	loader: async ({ context, params }) => {
		const profile = await ensureQueryDataSurvivingCancel(
			context.queryClient,
			getAccessProfileQueryOptions({ organizationId: params.orgId }),
		);
		if (!hasPermission(profile as UserAccessProfile, Action.MANAGE_LIVESTOCK)) {
			throw new ForbiddenError("Access denied to herd edit page");
		}
	},
	component: EditHerdPage,
});

function EditHerdPage() {
	const { orgId, establishmentId, herdId } = Route.useParams();
	const { focus } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: herd, isLoading } = useQuery(
		getHerdQueryOptions(orgId, herdId),
	);

	const updateMutation = useUpdateHerd();

	const handleSubmit = async (formData: UpdateHerdMutationRequest) => {
		return updateMutation.mutateAsync(
			{ organizationId: orgId, herdId, data: formData },
			{
				onSuccess: () => {
					navigate({
						to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId",
						params: { orgId, establishmentId, herdId },
					});
				},
			},
		);
	};

	if (isLoading) {
		return <FormSkeleton />;
	}

	if (!herd) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("livestock.herd.notFound")}</p>
			</div>
		);
	}

	return (
		// `-mt-3` cancels `<main>`'s `p-3` top gutter on phones (see the animal edit page).
		<div className="-mt-3 flex h-full flex-col gap-2 md:mt-0 md:gap-6">
			<PageHeader title={t("livestock.herd.edit.title")} showBackButton />
			<HerdForm
				herd={herd as LivestockHerd}
				establishmentId={establishmentId}
				focusDetail={focus}
				onSubmit={handleSubmit}
				isSubmitting={updateMutation.isPending}
				onCancel={() =>
					navigate({
						to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId",
						params: { orgId, establishmentId, herdId },
					})
				}
			/>
			<div className="max-w-4xl">
				<HerdAnimalsSection
					orgId={orgId}
					establishmentId={establishmentId}
					herdId={herdId}
				/>
			</div>
		</div>
	);
}
